import {
    Api, ArrayUtil, AuthToken, AutomationUtil, Collection, DisplayValue, DynamicLoader, HelpLink, HorizontalAlignment,
    ModelRow, ObjectUtil, ServerError, StringUtil
} from "@mcleod/core";
import { ImageName } from "@mcleod/images";
import {
    BehaviorTypes, Button, ComponentDeserializer, DataHeader, DataSource, DataSourceExecutionEvent, DataSourceMode,
    LayoutBehavior, LayoutHeader, LayoutLoadErrorEvent, LayoutLoadErrorListener, LayoutLoadEvent, LayoutLoadListener,
    LayoutLoadOutermostEvent, LayoutLoadOutermostListener, LayoutProvidedReportDecoratorProps,
    LayoutProvidedTaskDecoratorProps, MainPageAccessor, PanelStaticError, PropType, ScreenStack, Table
} from "../..";
import { Component } from "../../base/Component";
import { ComponentPropDefinition } from "../../base/ComponentProps";
import { ComponentTypes } from "../../base/ComponentTypes";
import { createDataSourcesFromDef } from "../../databinding/DataSource";
import { DesignableObjectLogManager } from "../../logging/DesignableObjectLogManager";
import { Decorator } from "../../page/decorators/Decorator";
import { DecoratorType } from "../../page/decorators/DecoratorType";
import { DeserializeProps } from "../../serializer/ComponentDeserializer";
import { Event, EventListener } from "../../events/Event";
import { LayoutCache } from "./LayoutCache";
import { LayoutCacheAccessor } from "./LayoutCacheAccessor";
import { LayoutDefinitionRetriever } from "./LayoutDefinitionRetriever";
import { LayoutPropDefinitions, LayoutProps } from "./LayoutProps";
import { LayoutProvidedCrudDecoratorProps } from "../../page/decorators/LayoutProvidedCrudDecoratorProps";
import { LayoutStyles } from "./LayoutStyles";
import { LayoutValidator } from "./LayoutValidator";
import { ListenerListDef } from "../../base/ListenerListDef";
import { Panel } from "../panel/Panel";
import { ReflectiveDialogs } from "../../base/ReflectiveDialogs";
import { Spinner } from "../spinner/Spinner";
import { WidgetTitleBar } from "./WidgetTitleBar";
import { TransitionOptions } from "../../base/TransitionOptions";

// These props need to be set before getting the definition from the server
const priorityProps: (keyof LayoutProps)[] = [
    "_designer",
    "applyFieldLevelPermissions",
    "applyFieldLevelLicensing",
    "applyFieldLevelCompanyType",
    "applyFieldLevelLtlType",
    "applyFieldLevelExperiment",
    "applyFieldLevelMultiCurrency",
    "needsServerLayout",
    "removedByServer",
    "isNested",
    "preventAutoSearchOnLoad"
];

/**
 * A Layout is a Panel subclass that gets its basic structure from a server-defined API call.  The server reads a .layout
 * file (in JSON format) and returns it to this class.  This class parses the result and adds the components defined in
 * the .layout.
 *
 * This .layout file is almost always created by the UI Designer page.
 *
 */
export class Layout extends Panel implements LayoutProps {
    public static shouldLoadVideoItemHelpLinks: boolean = true;
    private static _layoutBehavior: LayoutBehavior;
    private static readonly log = DesignableObjectLogManager.getLogger("components.Layout");
    private static readonly loadListenerDef: ListenerListDef = { listName: "_loadListeners" };
    private static readonly loadErrorDef: ListenerListDef = { listName: "_errorListeners" };
    private static readonly dataLoadListenerDef: ListenerListDef = { listName: "_dataLoadListeners" };
    private static readonly outermostLoadListenerDef: ListenerListDef = { listName: "_outermostLoadListeners" };
    private _layoutName: string;
    private _loaded: boolean;
    private outermostLayoutLoaded: boolean;
    private _loadError: string;
    private _dataLoaded: boolean;
    private _dataLoadDataSources: DataSource[];
    public auth: string;
    private _mainDataSource: DataSource;
    public needsServerLayout: boolean = true;
    private _title: string;
    private _titleImage: ImageName;
    private _titleAddModeSuffix: string;
    private _titleEditModeSuffix: string;
    private _titleSearchModeSuffix: string;
    private _hideTitle: boolean;
    private _hideSuffix: boolean;
    public applyFieldLevelPermissions: boolean;
    public applyFieldLevelLicensing: boolean;
    public applyFieldLevelCompanyType: boolean;
    public applyFieldLevelLtlType: boolean;
    public applyFieldLevelExperiment: boolean;
    public applyFieldLevelMultiCurrency: boolean;
    public dataSourceIDs: string[];
    private _isNested: boolean;
    private _inheritParentDataSource: string;
    private _loadPromise: Promise<any>;
    private _titleSinglular: string;
    private _titlePlural: string;
    private displayedLoadError;
    private _dataHeader: DataHeader;
    private layoutHeader: LayoutHeader;
    private _helpLinkRelatedElement: HTMLElement;
    private _helpLinks: HelpLink[];
    private _videoItemHelpLinks: HelpLink[];
    private _displayingFromToolbox: boolean;
    private _renderAsWidget: boolean;
    private _displayRefresh: boolean;
    private _widgetGroup: string;
    private _homePageSize: string;
    private _titleBar: WidgetTitleBar;
    private _widgetContentPanel: Panel;
    private _originalPropsBeforeWidget: any;
    private _layoutId: string;
    private _descr: string;
    private _allowedUses: DecoratorType[];
    private _defaultUse: DecoratorType;
    private _panelStaticError: PanelStaticError;
    private _displayingInViewMode: boolean;
    public mcleodOnly: boolean;
    public ideOnly: boolean;
    private _parameters: Record<string, any> = {};
    public preventAutoSearchOnLoad: boolean;
    public titleForModeOverrideFunction?: (title: string, mode: DataSourceMode) => string;

    constructor(props: Partial<LayoutProps> = {}) {
        super(props, false);
        this.setPriorityProps(props);
        this.setProps({
            fillRow: true,
            fillHeight: true,
            padding: 0,
            scrollX: LayoutPropDefinitions.getDefinitions().scrollX.defaultValue,
            scrollY: LayoutPropDefinitions.getDefinitions().scrollY.defaultValue,
            ...props
        });

        if (this.needsServerLayout === false) {
            this.ensureLoaded().then(() => {
                if (this.loadError == null)
                    this.loaded();
            });
        }
    }

    public get loadError(): string {
        return this._loadError;
    }

    set loadError(value: any) {
        if (value == null) {
            this._loadError = value;
            return;
        }

        Layout.log.info(this, value);

        if (value instanceof ServerError) {
            // Absence of errorTag means the server threw an exception that's appropriate to show to the user
            value = value.errorTag == null ? value.toString(): null;
        }

        this._loadError = ObjectUtil.toString(value, "Sorry, there was an error loading this page.");
        this.displayLoadErrorPanel();
        this.fireListeners(Layout.loadErrorDef, new LayoutLoadErrorEvent(this, this._loadError));
    }

    private setPriorityProps(props: Partial<LayoutProps>) {
        priorityProps.forEach(prop => {
            if (props[prop] != null) {
                this[prop] = props[prop];
                delete props[prop];
            }
        });
    }

    set layoutName(value: string) {
        if (this.layoutName === value)
            return;
        const oldValue = this._layoutName;
        this._layoutName = value;
        this.loadLayout();
        if (this._designer != null && this.isNested && this.deserialized) {
            const oldVal = StringUtil.stringAfterLast(oldValue, "/", false, oldValue);
            const newVal = StringUtil.stringAfterLast(value, "/", false, value);
            this._matchIdToValue(oldVal, newVal);
        }
    }

    get layoutName(): string {
        return this._layoutName;
    }

    get layoutId(): string {
        return this._layoutId;
    }

    set layoutId(value: string) {
        this._layoutId = value;
    }

    testingSpecificLayoutVersion(): boolean {
        return StringUtil.isEmptyString(this._layoutId) === false;
    }

    setProps(props: Partial<LayoutProps>) {
        super.setProps(props);
    }

    private loadLayout() {
        if (this.needsServerLayout === false) {
            this._loadPromise = this.loadLayoutWithoutDef().catch(error => {
                this.loadError = error;
            });
        } else {
            this.add(new Spinner());
            this._loadPromise = this.getLayoutDefinition().then(def => {
                return this.displayLayout(def);
            }).catch((error) => {
                if (this.isNested && this._designer != null)
                    this.removeAll();
                else {
                    this.loadError = error;
                }
            });
        }

        AutomationUtil.addLoadPromise(this._loadPromise);
    }

    private async getLayoutDefinition(): Promise<string> {
        const def = await new LayoutDefinitionRetriever().getLayoutDefinition(this);
        if (def instanceof ModelRow) {
            this.setDescrFromResponse(def);
            return def.get("definition");
        }
        return def;
    }

    private async loadLayoutWithoutDef() {
        new LayoutValidator(this).validate();
        this.retrieveVideoItemHelpLinks();
    }

    private displayLoadErrorPanel() {
        this.removeAll();
        const cls = DynamicLoader.getClassForPath("components/page/PanelStaticError");
        if (cls != null) {
            this.panelStaticError = new cls(this._loadError);
            this.add(this.panelStaticError);
        }
    }

    get panelStaticError(): PanelStaticError {
        return this._panelStaticError;
    }

    private set panelStaticError(value: PanelStaticError) {
        this._panelStaticError = value;
    }

    private setDescrFromResponse(rowLayout: ModelRow) {
        if (this._layoutId === "base")
            this._descr = "Base"
        else
            this._descr = rowLayout.get("descr", null);
    }

    private async loaded() {
        if (!this._loaded) {
            if (this._designer != null) {
                this.loadedInDesigner();
                return;
            }
            if (this.isNested && this.inheritParentDataSource != null)
                this.replaceMainDatasource(this.owner?.[this.inheritParentDataSource]);
            await this.setDataSourcePropsOnLoad();
            this._loaded = true;
            await this.onLoad();
            if (this.renderAsWidget === true)
                this.syncRenderAsWidget();
            const nestedLayouts: Layout[] = [];
            if (this.isNested !== true) {
                this.getRecursiveChildren()
                    .forEach((component: Component) => {
                        if (component instanceof Layout) {
                            nestedLayouts.push(component);
                        }
                        Layout.log.debug("Calling _doAfterOutermostLayoutLoaded() for component: %o", component.id);
                        component._doAfterOutermostLayoutLoaded();
                    });
            }
            this.fireListeners(Layout.loadListenerDef, new LayoutLoadEvent(this));
            this.handleLayoutLoadOutermost(nestedLayouts);
            if (this.isNested != true && this.parent != null) {
                ScreenStack.fireCurrentLayoutListeners();
            }
        }
    }

    /**
     * When a the outermost layout (the non-nested layout) finishes loading, do the following for each nested layout it
     * contains:
     * - Mark the outermostLayoutLoaded flag, so that the nested layout will know the outermost layout has finished
     *   loading.
     * - Fire the LayoutLoadOutermostEvent.
     * @param nestedLayouts The list of nested layouts contained within this layout (if any exist).
     */
    private handleLayoutLoadOutermost(nestedLayouts: Layout[]) {
        nestedLayouts?.forEach(nestedLayout => {
            nestedLayout.outermostLayoutLoaded = true;
            const event = new LayoutLoadOutermostEvent(nestedLayout, this);
            nestedLayout.fireListeners(Layout.outermostLoadListenerDef, event);
        });
    }

    private loadedInDesigner() {
        this._loaded = true;
        if (this.renderAsWidget === true)
            this.syncRenderAsWidget();
    }

    private async setDataSourcePropsOnLoad() {
        if (this._designer != null)
            return;

        for (const source of this._getDataSources()) {
            if (source.autoSearch === true) {
                source.mode = DataSourceMode.SEARCH;
            } else if (source.defaultMode != DataSourceMode.NONE) {
                source.mode = source.defaultMode;
            } else {
                await source.displayDefaultSearchData();
            }
        }
    }

    /**
     * This method is meant to be overridden by subclasses that might need to do something once all the components
     * are added to the screen.
     */
    onLoad(): void | Promise<void> {
    }

    addLayoutLoadListener(value: LayoutLoadListener) {
        value.runsOnce = true;
        this.addEventListener(Layout.loadListenerDef, value);
        if (this._loaded === true) {
            this.fireListeners(Layout.loadListenerDef, new LayoutLoadEvent(this));
        }
    }

    removeLayoutLoadListener(value: LayoutLoadListener) {
        this.removeEventListener(Layout.loadListenerDef, value);
    }

    /**
     * This listener behaves in a similar fashion as the layout load event.
     * - The event listener method can only be fired once.
     * - If the event listener is added before the outermost layout has finished loading, it doesn't fire until that
     *   outermost layout is done loading.
     * - If the event listener is added after the outermost layout has finished loading, it fires right away.
     * @param value 
     */
    addLayoutLoadOutermostListener(value: LayoutLoadOutermostListener) {
        value.runsOnce = true;
        this.addEventListener(Layout.outermostLoadListenerDef, value);
        if (this.outermostLayoutLoaded === true) {
            const outermostLayout = this.getRootLayout();
            const event = new LayoutLoadOutermostEvent(this, outermostLayout);
            this.fireListeners(Layout.outermostLoadListenerDef, event);
        }
    }

    removeLayoutLoadOutermostListener(value: LayoutLoadOutermostListener) {
        this.removeEventListener(Layout.outermostLoadListenerDef, value);
    }

    addLoadErrorListener(value: LayoutLoadErrorListener) {
        this.addEventListener(Layout.loadErrorDef, value);
        if (this._loadError != null) {
            this.fireListeners(Layout.loadErrorDef, new LayoutLoadErrorEvent(this, this._loadError));
        }
    }

    removeLoadErrorListener(value: LayoutLoadErrorListener) {
        this.removeEventListener(Layout.loadErrorDef, value);
    }

    /**
     * A Layout's data load listeners are meant to fire after the layout has been loaded, AND
     * all DataSources within the layout (including nested layouts) have finished searching.
     * This is done by:
     *  -> Gathering all DataSources (including those from nested layouts)
     *  -> Determining which of those DataSources are top-level DataSources
     *  -> See if any top-level DataSources are currently searching.
     *     Each DataSource still searching has an after execute listener added to it so it can notify us when it's done.
     *  -> DataSources that are not at the top-level (those that are a child of another DataSource) are handled by the DataSource class
     *     (since we use the afterSearchPlusChildren listener)
     *  -> DataSources that begin to search after this point are added to the list of DataSources and get a similar listener.
     *     This is the purpose of the DataSource's trackingLayout; it lets the DataSource be tied to these functions in the Layout.
     *  -> When no DataSources are still executing (when the array is empty), fire the data load listeners.
     *
     * Note that data load listeners added after the above process has taken place will run immediately.
     *
     * @param value the method to call when both the layout and the layout's data has been loaded
     */
    addLayoutDataLoadListener(value: EventListener) {
        value.runsOnce = true;
        const isFirstListener = !this.hasListeners(Layout.dataLoadListenerDef) && this._dataLoaded !== true;
        this.addEventListener(Layout.dataLoadListenerDef, value);
        if (this._loaded === true && this._dataLoaded === true) {
            this.fireListeners(Layout.dataLoadListenerDef, new Event(this));
            return;
        }
        if (isFirstListener === true) {
            const topLevelDataLoadDataSources = DataSource.getTopLevelDataSources(this._dataLoadDataSources);
            this._dataLoadDataSources = null;
            if (ArrayUtil.isEmptyArray(topLevelDataLoadDataSources) === true) {
                this._dataLoaded = true;
                this._fireDataLoadedListeners();
                return;
            }
            for (const dataSource of topLevelDataLoadDataSources) {
                dataSource.trackingLayout = this;
                if (dataSource.isSearchingOrChildrenSearching === true)
                    this.addDataLoadDataSource(dataSource);
            }
        }
    }

    removeLayoutDataLoadListener(value: EventListener) {
        this.removeEventListener(Layout.dataLoadListenerDef, value);
    }

    addDataLoadDataSource(dataSource: DataSource) {
        if (this._dataLoaded === true) {
            dataSource.trackingLayout = null;
            return;
        }
        if (this._dataLoadDataSources == null)
            this._dataLoadDataSources = [];
        if (ArrayUtil.addNoDuplicates(this._dataLoadDataSources, dataSource) === true)
            this._addDataLoadDataSourceListener(dataSource);
    }

    private _addDataLoadDataSourceListener(dataSource: DataSource) {
        const callback: EventListener = (event: DataSourceExecutionEvent) => this._removeDataLoadDataSource(this, event.dataSource);
        callback.runsOnce = true;
        dataSource.addAfterSearchPlusChildrenListener(callback);
    }

    private _removeDataLoadDataSource(layout: Layout, dataSource: DataSource) {
        ArrayUtil.removeFromArray(layout._dataLoadDataSources, dataSource);
        if (layout._dataLoaded === true)
            return;
        if (ArrayUtil.isEmptyArray(layout._dataLoadDataSources) === true) {
            layout._dataLoaded = true;
            this._fireDataLoadedListeners(layout);
        }
    }

    private _fireDataLoadedListeners(layout: Layout = this) {
        layout.addLayoutLoadListener((_event: LayoutLoadEvent) => {
            layout.fireListeners(Layout.dataLoadListenerDef, new Event(layout));
        });
    }

    override getPropertyDefinitions() {
        if (this.isNested)
            return {
                ...LayoutPropDefinitions.getDefinitions(), id: {
                    type: PropType.string,
                    description: "The identifier for this nested layout."
                }
            }
        return LayoutPropDefinitions.getDefinitions();
    }

    async search(filter: any): Promise<any> {
        if (this.mainDataSource == null)
            throw new Error("Can't search a layout without a mainDataSource.");
        return this.mainDataSource.search(filter);
    }

    public async ensureLoaded(): Promise<any> {
        if (this._loadPromise != null)
            return this._loadPromise;
        else
            return Promise.resolve(null);
    }

    private async displayLayout(defStr: string) {
        try {
            this.removeAll();
            const def = JSON.parse(defStr);
            let dataSources: Collection<DataSource>;
            const dataSourceIDs = [];
            if (def.dataSources != null) {
                const sources = createDataSourcesFromDef(def.dataSources, this, this.layoutName);
                dataSources = {};
                for (const dataSource of sources) {
                    dataSources[dataSource.id] = dataSource;
                    dataSourceIDs.push(dataSource.id);
                    this[dataSource.id] = dataSource;
                    // Getting the metadata here is now a redundant second attempt, as the metadata should have been
                    // included in the retrieval of the layout.
                    await dataSource.getMetadata();
                }
            }
            for (const key in LayoutPropDefinitions.getDefinitions()) {
                let value = def[key];
                if (value != null) {
                    if (key === "mainDataSource")
                        value = dataSources[value];
                    this[key] = value;
                }
            }

            const defaultProps = this.removedByServer ? { removedByServer: true } : null;
            const components = await new ComponentDeserializer({
                owner: this,
                def: def.components,
                designer: this._designer,
                defaultPropValues: defaultProps,
                dataSources: dataSources
            }).deserialize();

            await this.deserializeRemovedComponents(def);

            for (const comp of components) {
                if (!(comp instanceof DataHeader))
                    this.add(comp);
                else
                    this.dataHeader = comp;
            }

            this.dataSourceIDs = [...dataSourceIDs];
            this._dataLoadDataSources = this._getDataSources();
            const childLayoutVideoItemHelpLinks: HelpLink[] = [];
            const tablesByLayout: any = {};
            for (const comp of this.getRecursiveChildren()) {
                if (comp instanceof Layout) {
                    this._dataLoadDataSources.push(...comp._getDataSources());
                    if (comp.videoItemHelpLinks != null)
                        childLayoutVideoItemHelpLinks.push(...comp.videoItemHelpLinks);
                }
                else if (comp instanceof Table) {
                    //discover tables so we can use the user's default config (if one is present)
                    const layout = comp.getParentLayout();
                    if (tablesByLayout[layout.layoutName] == null)
                        tablesByLayout[layout.layoutName] = [];
                    tablesByLayout[layout.layoutName].push(comp);
                } else if (comp.serializationName === "layoutheader" && this.isNested !== true) {
                    if (comp.owner == this)
                        this.layoutHeader ??= comp as LayoutHeader;
                }
            }
            this.deserializeVideoItemHelpLinks(def.videoItemHelpLinks);
            this.addVideoItemHelpLink(...childLayoutVideoItemHelpLinks);
            for (const layoutName of Object.keys(tablesByLayout)) {
                const tables = tablesByLayout[layoutName] as Table[];
                for (const table of tables) {
                    table.applyDefaultConfig(layoutName);
                }
            }
            this.layoutBehavior.setDeviceBehaviors(this);

            await this.loaded();
        }
        catch (err) {
            Layout.log.error(this, "Error loading page", err);
            if (this.displayedLoadError !== true) {
                this.displayedLoadError = true;
                ReflectiveDialogs.showError(err);
            }
        }
    }

    private async deserializeRemovedComponents(def: any) {
        await new ComponentDeserializer({
            owner: this,
            def: def.removed,
            designer: this._designer,
            defaultPropValues: { removedByServer: true },
        }).deserialize();
    }

    override add(...components: Component[]): Component {
        if (this.shouldAddToContentPanel(...components))
            return this._widgetContentPanel.add(...components)
        return super.add(...components);
    }

    shouldAddToContentPanel(...components: Component[]): boolean {
        return this._widgetContentPanel != null && components?.length > 0 && components[0] != this._widgetContentPanel && components[0] != this._titleBar;
    }

    private _getDataSources(): DataSource[] {
        const result: DataSource[] = [];
        if (this.dataSourceIDs != null) {
            for (const dataSourceID of this.dataSourceIDs) {
                result.push(this[dataSourceID] as DataSource);
            }
        }
        return result;
    }

    get title(): string {
        return this.getTitleForMode(this.mainDataSource?.mode);
    }

    set title(value: string) {
        this._title = value;
        this.titleBar?.setTitle(this.title);
        if (this._designer != null) {
            this.findComponentByType(LayoutHeader)?.syncTitle();
        }
    }

    get windowTitle(): string {
        if (this.testingSpecificLayoutVersion() === true && StringUtil.isEmptyString(this._descr) === false)
            return this.title + " - " + this._descr;
        else if (StringUtil.isEmptyString(this.dataHeader?.title) !== true)
            return this.dataHeader.title;
        else if (StringUtil.isEmptyString(this.layoutHeader?.title) !== true)
            return this.layoutHeader.title;

        return this.title;
    }

    public getTitleForMode(mode: DataSourceMode): string {
        if (this.titleForModeOverrideFunction) {
            return this.titleForModeOverrideFunction(this._title, mode);
        }
        if (mode == null)
            mode = DataSourceMode.NONE;
        const row = this.mainDataSource?.activeRow;
        let result = DisplayValue.getFormattedDataString(this._title, row);
        let suffix = null;
        switch (mode) {
            case DataSourceMode.UPDATE: suffix = DisplayValue.getFormattedDataString(this.titleEditModeSuffix, row); break;
            case DataSourceMode.ADD: suffix = DisplayValue.getFormattedDataString(this.titleAddModeSuffix, row); break;
            case DataSourceMode.SEARCH: suffix = DisplayValue.getFormattedDataString(this.titleSearchModeSuffix, row); break;
        }
        if (!this._hideSuffix && !StringUtil.isEmptyString(suffix))
            result += " - " + suffix;
        return result;
    }

    get titleImage(): ImageName {
        return this._titleImage;
    }

    set titleImage(value: ImageName) {
        this._titleImage = value;
    }

    get titleAddModeSuffix(): string {
        return this._titleAddModeSuffix || this.getDefaultTitleAddModeSuffix();
    }

    set titleAddModeSuffix(value: string) {
        this._titleAddModeSuffix = value;
    }

    private getDefaultTitleAddModeSuffix() {
        return "New";
    }

    get titleEditModeSuffix(): string {
        return this._titleEditModeSuffix || this.getDefaultTitleEditModeSuffix();
    }

    set titleEditModeSuffix(value: string) {
        this._titleEditModeSuffix = value;
    }

    private getDefaultTitleEditModeSuffix(): string {
        let result = "Edit";
        if (this.mainDataSource instanceof DataSource) {
            let displayFormat = this.mainDataSource?.getMetadataFromCache()?.displayField;
            if (displayFormat != null && displayFormat.indexOf("{") < 0)
                displayFormat = "{" + displayFormat + "}";
            if (displayFormat != null)
                result += " - " + displayFormat;
        }
        return result;
    }

    get titleSearchModeSuffix() {
        return this._titleSearchModeSuffix || this.getDefaultTitleSearchModeSuffix();
    }

    set titleSearchModeSuffix(value: string) {
        this._titleSearchModeSuffix = value;
    }

    private getDefaultTitleSearchModeSuffix(): string {
        return "Advanced Search";
    }

    get hideTitle(): boolean {
        return this._hideTitle;
    }

    set hideTitle(value: boolean) {
        this._hideTitle = value;
    }

    get hideSuffix(): boolean {
        return this._hideSuffix;
    }

    set hideSuffix(value: boolean) {
        this._hideSuffix = value;
    }

    override get serializationName() {
        return "layout";
    }

    override get properName(): string {
        return "Layout";
    }

    public setAllDataSourcesToMode(mode: DataSourceMode): void {
        this.dataSourceIDs.forEach((id) => {
            if ((this.mainDataSource == null) || (id !== this.mainDataSource.id))
                (this[id] as DataSource).mode = mode;
        });
        if (this.mainDataSource != null)
            this.mainDataSource.mode = mode;
    }

    public recursiveSetAllDataSourcesToMode(mode: DataSourceMode): void {
        this.setAllDataSourcesToMode(mode);
        this.findComponentsByType(Layout).forEach(l => l.recursiveSetAllDataSourcesToMode(mode));
    }

    public clearNonMainDataSources() {
        for (const dataSourceID of this.dataSourceIDs) {
            if (dataSourceID === this.mainDataSource.id)
                continue;
            const dataSource = this[dataSourceID] as DataSource;
            dataSource.clear();
        }
    }

    public get titleSingular() {
        return this._titleSinglular || this.getDefaultTitleSingular();
    }

    public set titleSingular(value: string) {
        this._titleSinglular = value;
    }

    private getDefaultTitleSingular(): string {
        if (!this._titlePlural && !this._title)
            return undefined;

        const title = this._title || this._titlePlural;

        if (title.endsWith("ies"))
            return title.substring(0, title.length - 3) + "y";
        if (title.endsWith("ses") || title.endsWith("ches") || title.endsWith("shes"))
            return title.substring(0, title.length - 2);
        if (title.endsWith("s") && !title.endsWith("ss"))
            return title.substring(0, title.length - 1);

        return title;
    }

    public get titlePlural() {
        return this._titlePlural || this.getDefaultTitlePlural();
    }

    public set titlePlural(value: string) {
        this._titlePlural = value;
    }

    private getDefaultTitlePlural(): string {
        if (this._title != null)
            return this._title;
        if (this._titleSinglular != null)
            return this._titleSinglular + "s";
        return undefined;
    }

    _serializeNonProps(): string {
        if (this.components != null && this.components.length > 0 && this.isNested !== true)
            return super._serializeNonProps();
        return "";
    }

    _serializeProp(key: string, value: string): string {
        if ("videoItemHelpLinks" === key) //never serialize videoItemHelpLinks; they come from the database
            return null;
        if (this.isNested && "helpLinks" === key)
            return null;
        if (this._originalPropsBeforeWidget != null && key in this._originalPropsBeforeWidget)
            return this._originalPropsBeforeWidget[key];
        return value;
    }

    public get isNested(): boolean {
        return this._isNested;
    }

    public set isNested(value: boolean) {
        this._isNested = value;
    }

    protected override internalRelayout() {
        super.internalRelayout();
        if (this.isNested && this._designer != null && !this.owner?.isNested)
            this._element.appendChild(this.createNestedDesignerOverlay());
    }

    private createNestedDesignerOverlay() {
        const result = new Panel({ id: "nested-designer-overlay", className: LayoutStyles.nestedDesignerOverlay });
        const button = new Button({
            caption: "Nested Layout ",
            borderWidth: 0,
            imageName: ImageName.detail,
            tooltip: "Open " + this.layoutName + " in another designer tab",
            imageHeight: 32,
            imageWidth: 32,
            align: HorizontalAlignment.CENTER,
            fontSize: 40
        });
        if (this._designer?.openTab != null)
            button.addClickListener(() => this._designer.openTab({path: this.layoutName}, true));
        result.add(button);
        return result._element;
    }

    public get inheritParentDataSource(): string {
        return this._inheritParentDataSource;
    }

    public set inheritParentDataSource(value: string) {
        this._inheritParentDataSource = value;
    }

    override getListenerDefs(): Collection<ListenerListDef> {
        return {
            ...super.getListenerDefs(),
            "loadListener": { ...Layout.loadListenerDef },
            "loadError": { ...Layout.loadErrorDef },
            "dataLoadListener": { ...Layout.dataLoadListenerDef },
            "outermostLoadListener": { ...Layout.outermostLoadListenerDef }
        };
    }

    public get mainDataSource(): DataSource {
        return this._mainDataSource;
    }

    public set mainDataSource(value: DataSource) {
        this._mainDataSource = value;
    }

    protected override determinePropertyDefaultValue(prop: ComponentPropDefinition) {
        if (prop.name === "titleSingular")
            return this.getDefaultTitleSingular();
        if (prop.name === "titlePlural")
            return this.getDefaultTitlePlural();
        if (prop.name === "titleAddModeSuffix")
            return this.getDefaultTitleAddModeSuffix();
        if (prop.name === "titleEditModeSuffix")
            return this.getDefaultTitleEditModeSuffix();
        if (prop.name === "titleSearchModeSuffix")
            return this.getDefaultTitleSearchModeSuffix();
        return super.determinePropertyDefaultValue(prop);
    }

    override async _deserializeSpecialProps(props: DeserializeProps): Promise<string[]> {
        const mainDataSourceId = props.def.mainDataSource;
        if (mainDataSourceId != null && props.dataSources != null)
            this.mainDataSource = props.dataSources[mainDataSourceId];
        return ["mainDataSource", ...await super._deserializeSpecialProps(props)];
    }

    public replaceMainDatasource(newDatasource: DataSource) {
        if (newDatasource != null) {
            this.mainDataSource.rebindComponentsTo(newDatasource);
            newDatasource.attachListeners(this.mainDataSource);
            for (const dataSourceID of this[this.mainDataSource.id].getChildDataSourceIds())
                this[dataSourceID].parentDataSource = newDatasource;

            this.mainDataSource = newDatasource;
            this[this.mainDataSource.id] = newDatasource;
        }
    }

    private retrieveVideoItemHelpLinks() {
        if (!Layout.shouldLoadVideoItemHelpLinks || AuthToken.isAuthenticated() !== true)
            return;
        Api.search("video-item-help-links", { path: this.layoutName }).then(response => {
            const helpLinkDefs = response.data[0]?.video_help_links;
            this.deserializeVideoItemHelpLinks(helpLinkDefs);
            const currentContainer = ScreenStack.getCurrentLayoutContainer();
            const currentLayouts = ScreenStack.getCurrentLayouts(currentContainer);
            // have to tell the page header that help links were added (after the layout was added to the screen stack)
            MainPageAccessor.def.evaluateExternalLinkVisibility(currentContainer as Panel, currentLayouts);
        }).catch(_error => {
            Layout.log.debug(this, "Unable to retrieve video item help links for client-defined layout %o",
                this.layoutName);
        });
    }

    private deserializeVideoItemHelpLinks(videoItemHelpLinksDef: any[]) {
        if (videoItemHelpLinksDef == null)
            return;
        for (const def of videoItemHelpLinksDef) {
            if (StringUtil.isEmptyString(def.caption) === false && StringUtil.isEmptyString(def.id) === false)
                this.addVideoItemHelpLink(new HelpLink(def.caption, def.id, () => this.getHelpLinkRelatedElement()));
        }
    }

    public static clearCachedLayout(name: string): void {
        LayoutCacheAccessor.clear(name);
    }

    public static getLayout(layoutName: string, props?: Partial<LayoutProps>): Layout {
        let constructorProps: Partial<LayoutProps> = null;
        if (props != null && props["layoutId"] != null) {
            //user is testing a specific version of a custom layout
            //load that specific version of the layout (and don't pull from or update the layout cache)
            //this requires setting the layoutId before setting the layoutName, since setting the layoutName causes the layout to be loaded
            const layoutId = props["layoutId"];
            delete props["layoutId"];
            constructorProps = { layoutId: layoutId, layoutName: layoutName, ...props };
        }
        else
            constructorProps = { layoutName: layoutName, ...props };

        const cls = DynamicLoader.getClassForPath(layoutName);
        if (cls == null)
            return new Layout(constructorProps)
        else
            return new cls(constructorProps);
    }

    /**
     * Waits on the specified layout to be loaded, then returns it.
     *
     * @param layoutName The name of the layout to load
     * @param props Any LayoutProps to assign to the layout
     * @returns The loaded layout
     */
    public static async getLoadedLayout(layoutName: string, props?: Partial<LayoutProps>): Promise<Layout> {
        const layout = Layout.getLayout(layoutName, props);

        layout.addLoadErrorListener(() => {
            throw new Error(layout.loadError);
        });

        await layout.ensureLoaded();
        return layout;
    }

    /**
     * Waits on the specified layout to be loaded, and for the layout to load data, then returns it.
     *
     * @param layoutName The name of the layout to load
     * @param props Any LayoutProps to assign to the layout
     * @param idOrData Either the id of the record to load in the layout (a string value), or a ModelRow to display in
     *                 the layout, or an array of ModelRows to display in the layout
     * @param searchFilter A json object containing the values to use when searching the layout's main DataSource.  This
     *                     is used when the idOrData parameter does not contain a ModelRow or an array of ModelRows;
     *                     when that happens, the searchFilter will be used to search the layout's main DataSource.
     * @returns The loaded layout, populated with data
     */
    public static async getLoadedLayoutWithData(layoutName: string, layoutProps: Partial<LayoutProps>,
        idOrData: string | ModelRow<any> | ModelRow<any>[], searchFilter?: any): Promise<Layout> {
        if (StringUtil.isEmptyString(layoutName)) {
            return null;
        }
        const layout = await Layout.getLoadedLayout(layoutName, layoutProps);
        if (idOrData instanceof ModelRow) {
            layout.mainDataSource.data = [idOrData];
            layout.mainDataSource.rowIndex = 0;
        }
        else if (Array.isArray(idOrData)) {
            layout.mainDataSource.setRowsAndMode(DataSourceMode.NONE, idOrData);
        }
        else if (!StringUtil.isEmptyString(idOrData) || searchFilter != null) {
            if (searchFilter == null) {
                searchFilter = { id: idOrData };
            }
            await layout.mainDataSource.search(searchFilter);
        }
        return layout;
    }

    public static async canAccessLayout(layoutName: string): Promise<boolean> {
        if (LayoutCache.isCached(layoutName))
            return true;

        try {
            await Layout.getLoadedLayout(layoutName);
            return true;
        } catch {
            return false;
        }
    }

    get dataHeader(): DataHeader {
        return this._dataHeader;
    }

    set dataHeader(value: DataHeader) {
        this._dataHeader = value;
    }

    public doAfterDataHeaderSetup(dataHeader: DataHeader) {}

    /**
     * Override so that a layout can first check to see if it's a child of a Decorator...if it is, slide that decorator out instead
     * @param options
     * @param componentToFocusOnClose
     * @returns
     */
    public override slideOut(options?: TransitionOptions, componentToFocusOnClose?: Component): Promise<any> {
        const decorator = this.getParentDecorator();
        if (decorator == null)
            return super.slideOut(options, componentToFocusOnClose);
        else
            return decorator.slideOut(options, componentToFocusOnClose);
    }

    private getParentDecorator(): Decorator {
        let parent = this.parent;
        while (parent != null) {
            if (parent instanceof Decorator)
                return parent;
            parent = parent.parent;
        }
        return null;
    }

    public get activeRow(): ModelRow<any> {
        return this.mainDataSource?.activeRow;
    }

    //help links are handled oddly only because of the limitations of how PropertiesTable
    //handles property values from specialty editors.  we can eventually make this look not-so-dodgy.
    public get helpLinks(): string {
        if (this._helpLinks == null)
            return null;
        return JSON.stringify(this._helpLinks);
    }

    public set helpLinks(value: string) {
        if (StringUtil.isEmptyString(value) === true)
            this._helpLinks = null;
        const defs = JSON.parse(value);
        if (ArrayUtil.isEmptyArray(defs) === false) {
            this._helpLinks = [];
            for (const def of defs) {
                this.addHelpLink(new HelpLink(def.text, def.url, () => this.getHelpLinkRelatedElement()));
            }
        }
        else
            this._helpLinks = null;
    }

    public addHelpLink(value: HelpLink) {
        if (this._helpLinks == null)
            this._helpLinks = [];
        this._helpLinks.push(value);
    }

    public get videoItemHelpLinks(): HelpLink[] {
        return this._videoItemHelpLinks;
    }

    public set videoItemHelpLinks(value: HelpLink[]) {
        this._videoItemHelpLinks = value;
    }

    public addVideoItemHelpLink(...value: HelpLink[]) {
        if (this.videoItemHelpLinks == null)
            this.videoItemHelpLinks = [];
        ArrayUtil.addAllNoDuplicates(this.videoItemHelpLinks, value);
    }

    public getAllHelpLinks(): HelpLink[] {
        const result = this.videoItemHelpLinks == null ? [] : [...this.videoItemHelpLinks];
        ArrayUtil.addAllNoDuplicates(result, this._helpLinks);
        return result;
    }

    private getHelpLinkRelatedElement(): HTMLElement {
        if (this._helpLinkRelatedElement == null) {
            const enclosingTabsOrSteps = this.findAllParentsOfType("tab", "step");
            if (ArrayUtil.isEmptyArray(enclosingTabsOrSteps) === false &&
                enclosingTabsOrSteps.every((tabOrStep: Component) => tabOrStep.visible === true)) {
                this._helpLinkRelatedElement = enclosingTabsOrSteps[0].parent._element;
            }
            if (this._helpLinkRelatedElement == null)
                this._helpLinkRelatedElement = this._element;
        }
        return this._helpLinkRelatedElement;
    }

    public get displayingFromToolbox(): boolean {
        return this._displayingFromToolbox == null ? false : this._displayingFromToolbox;
    }

    public set displayingFromToolbox(value: boolean) {
        this._displayingFromToolbox = value;
    }

    public get renderAsWidget(): boolean {
        return this._renderAsWidget == null ? false : this._renderAsWidget;
    }
    public set renderAsWidget(value: boolean) {
        this._renderAsWidget = value;
        this.syncRenderAsWidget();
    }
    public get displayRefresh(): boolean {
        return this._displayRefresh;
    }
    public set displayRefresh(value: boolean) {
        this._displayRefresh = value;
    }
    public get widgetGroup(): string {
        return this._widgetGroup;
    }
    public set widgetGroup(value: string) {
        this._widgetGroup = value;
    }
    public get homePageSize(): string {
        return this._homePageSize || this.getPropertyDefinitions().homePageSize.defaultValue;
    }
    public set homePageSize(value: string) {
        this._homePageSize = value;
    }
    public get titleBar(): WidgetTitleBar {
        return this._titleBar;
    }

    get busyWhenDataSourceBusy(): boolean {
        return this._renderAsWidget === true;
    }

    private syncRenderAsWidget() {
        if (this._loaded !== true) return;

        if (this._renderAsWidget != true && this._titleBar != null) {
            this.setProps({ ...this._originalPropsBeforeWidget });
            this._originalPropsBeforeWidget = null;
            this.remove(this._titleBar);
            this.remove(this._widgetContentPanel);
            const components = this._widgetContentPanel.components;
            this._widgetContentPanel = null;
            this.add(...components);
            this._titleBar = null;
            this.mainDataSource?.removeBusyComponent(this);
        } else if (this._renderAsWidget === true && this._titleBar == null) {
            this.mainDataSource?.addBusyComponent(this);
            this._originalPropsBeforeWidget = this.replaceProps({ borderColor: "strokeSecondary", borderShadow: false, borderRadius: 10, borderWidth: 1, padding: 0, scrollY: this.scrollY });
            this.style.overflowY = "hidden";
            const components = this.components;
            this.removeAll();
            this._widgetContentPanel = new Panel({ padding: 0, fillRow: true, fillHeight: true, components: [...components] });
            this.add(this._widgetContentPanel);
            this._titleBar = new WidgetTitleBar(this);
            this.insert(this._titleBar, 0);
        }
    }

    public get busy(): boolean {
        return super.busy;
    }

    public set busy(value: boolean) {
        if (this.renderAsWidget === true) {
            // When the layout is a widget, we don't want to set the whole layout as busy.  Instead, only the widget's
            // content area should become busy.
            this._widgetContentPanel.busy = value;
        }
        else
            super.busy = value;
    }

    public get parameters(): Record<string, any> {
        return this._parameters;
    }

    public set parameters(keyValuePairs: Record<string, any>) {
        this._parameters = { ...this._parameters, ...keyValuePairs };
    }

    public get crudDecoratorProps(): Partial<LayoutProvidedCrudDecoratorProps> {
        return {};
    }

    public get reportDecoratorProps(): Partial<LayoutProvidedReportDecoratorProps> {
        return {};
    }

    public get taskDecoratorProps(): Partial<LayoutProvidedTaskDecoratorProps> {
        return {};
    }

    private get layoutBehavior(): LayoutBehavior {
        Layout._layoutBehavior ??= BehaviorTypes.get("Layout");
        return Layout._layoutBehavior;
    }

    // The allowedUses value is handled as a string because of the limitations of how PropertiesTable handles property
    // values from specialty editors.  We can eventually make this look not-so-dodgy.
    get allowedUses(): string {
        if (this.isNested === true || ArrayUtil.isEmptyArray(this._allowedUses)) {
            return null;
        }
        const result = {
            uses: this._allowedUses,
            defaultUse: this.defaultUse
        };
        if (result.defaultUse == null) {
            delete result.defaultUse;
        }
        return JSON.stringify(result);
    }

    set allowedUses(value: string) {
        if (StringUtil.isEmptyString(value) === true) {
            this._allowedUses = null;
            this.defaultUse = null;
            return;
        }
        const def = JSON.parse(value);
        this._allowedUses = def.uses;
        this.defaultUse = def.defaultUse;
    }

    // We wouldn't need this method except for the fact that 'get allowedUses()' needs to return a string for property
    // serialization/deserializaion.
    public getAllowedUses(): DecoratorType[] {
        return this._allowedUses ?? [];
    }

    public get defaultUse(): DecoratorType {
        return this.isNested !== true ? this._defaultUse : null;
    }

    private set defaultUse(value: DecoratorType) {
        this._defaultUse = value;
    }

    override displayInViewMode() {
        this.addLayoutLoadListener(() => this.displayInViewModeAfterLoad());
    }

    public get displayingInViewMode(): boolean {
        return this._displayingInViewMode;
    }

    private set displayingInViewMode(value: boolean) {
        this._displayingInViewMode = value;
    }

    protected displayInViewModeAfterLoad() {
        this.displayingInViewMode = true;
        super.displayInViewMode();
    }
}

ComponentTypes.registerComponentType("layout", {
    constructor: Layout.prototype.constructor,
    isCompound: false,
    constructorProps: ["layoutName", "isNested"]
});
